import React, { useState, useEffect, useRef } from 'react';
import Layout from '../../components/Layout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { SearchableSelect } from '../../components/ui/searchable-select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Checkbox } from '../../components/ui/checkbox';
import api from '../../utils/api';
import { Plus, Edit, Trash2, Search, Car, MapPin, Route, Filter, X, Download, Upload, FileSpreadsheet, IndianRupee, Calendar, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import * as ExcelJS from 'exceljs';

// Default vehicle types (fallback if masters not loaded)
const DEFAULT_VEHICLE_TYPES = [
  { value: 'Sedan', label: 'Sedan', capacity: 4 },
  { value: 'SUV', label: 'SUV', capacity: 6 },
  { value: 'Innova', label: 'Innova', capacity: 7 },
  { value: 'Ertiga', label: 'Ertiga', capacity: 6 },
  { value: 'Dzire', label: 'Dzire', capacity: 4 },
  { value: 'Fortuner', label: 'Fortuner', capacity: 7 },
  { value: 'Tempo Traveller', label: 'Tempo Traveller', capacity: 12 },
  { value: 'Mini Bus', label: 'Mini Bus', capacity: 20 },
  { value: 'Luxury Bus', label: 'Luxury Bus', capacity: 40 },
];

const GST_SLABS = [
  { value: 0, label: '0%' },
  { value: 5, label: '5%' },
  { value: 12, label: '12%' },
];

const TransferManager = () => {
  const [transfers, setTransfers] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [places, setPlaces] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState(DEFAULT_VEHICLE_TYPES);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [editingTransfer, setEditingTransfer] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('city');
  const [showFilters, setShowFilters] = useState(false);
  
  // Point-to-Point state
  const [p2pTransfers, setP2pTransfers] = useState([]);
  const [p2pDialogOpen, setP2pDialogOpen] = useState(false);
  const [editingP2p, setEditingP2p] = useState(null);
  const [p2pDeleteConfirm, setP2pDeleteConfirm] = useState({ open: false, id: null, name: '' });
  const [p2pBulkDialogOpen, setP2pBulkDialogOpen] = useState(false);
  const [p2pUploading, setP2pUploading] = useState(false);
  const p2pFileInputRef = useRef(null);
  
  // P2P Masters
  const [p2pMasters, setP2pMasters] = useState({
    transportModes: ['Road', 'Ferry', 'Train', 'Flight', 'Boat', 'Cable Car', 'Cruise'],
    transferCategories: ['Pickup', 'Drop', 'Sightseeing', 'Intercity', 'Inter-Island', 'Airport Transfer', 'Railway Transfer', 'Port Transfer'],
    inclusionTypes: ['Mandatory', 'Optional', 'Upgrade'],
    gstSlabs: ['0', '5', '12', '18'],
    currencies: ['INR', 'USD', 'EUR', 'GBP', 'AED'],
  });
  
  const [p2pForm, setP2pForm] = useState({
    // Location
    from_place: '',
    to_place: '',
    from_place_id: '',
    to_place_id: '',
    // Transport
    transport_mode: 'Road',
    vehicle_type: '',
    vehicle_type_id: '',
    // Classification
    transfer_category: 'Intercity',
    description: '',
    // Common Settings
    pricing_type: 'PER_VEHICLE',
    currency: 'INR',
    gst_slab: 5,
    includes_gst: false,
    // Validity
    valid_from: '',
    valid_to: '',
    season_name: '',
    // Supplier
    supplier_id: '',
    // Remarks
    remarks: '',
  });
  
  // Pricing Tiers - array of tier entries
  const [pricingTiers, setPricingTiers] = useState([
    { variant_name: '', rate: '', cost_rate: '', max_pax: 4, inclusion_type: 'Mandatory' }
  ]);
  
  // Filters
  const [filters, setFilters] = useState({
    supplier_id: '',
    vehicle_type: '',
    arrival_city: '',
  });
  
  const [formData, setFormData] = useState({
    transfer_type: 'CITY_TO_CITY',
    arrival_city: '',
    departure_city: '',
    route_id: '',
    supplier_id: '',
    vehicle_type: '',
    vehicle_label: '',
    max_pax_capacity: '',
    rate: '',
    gst_slab: 5,
    contract_type: 'PER_DAY',
    date_from: '',
    date_to: '',
  });

  // Bulk entry form - will be initialized after fetching vehicle types
  const [bulkData, setBulkData] = useState({
    transfer_type: 'CITY_TO_CITY',
    arrival_city: '',
    departure_city: '',
    route_id: '',
    supplier_id: '',
    date_from: '',
    date_to: '',
    gst_slab: 5,
    vehicles: [],
  });

  useEffect(() => {
    fetchTransfers();
    fetchRoutes();
    fetchPlaces();
    fetchSuppliers();
    fetchVehicleTypes();
    fetchP2pTransfers();
    fetchP2pMasters();
  }, []);

  // Update bulk form when vehicle types are loaded
  useEffect(() => {
    if (vehicleTypes.length > 0) {
      setBulkData(prev => ({
        ...prev,
        vehicles: vehicleTypes.map(v => ({
          vehicle_type: v.value,
          vehicle_label: `${v.label} (${v.capacity} pax)`,
          max_pax_capacity: v.capacity,
          rate: '',
          selected: false,
        })),
      }));
    }
  }, [vehicleTypes]);

  const fetchTransfers = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.supplier_id) params.append('supplier_id', filters.supplier_id);
      if (filters.vehicle_type) params.append('vehicle_type', filters.vehicle_type);
      if (filters.arrival_city) params.append('arrival_city', filters.arrival_city);
      
      const response = await api.get(`/transfers?${params.toString()}`);
      setTransfers(response.data);
    } catch (error) {
      toast.error('Failed to load transfers');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoutes = async () => {
    try {
      const response = await api.get('/routes');
      setRoutes(response.data);
    } catch (error) {
      console.error('Failed to load routes');
    }
  };

  const fetchPlaces = async () => {
    try {
      const response = await api.get('/places');
      setPlaces(response.data);
    } catch (error) {
      console.error('Failed to load places');
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await api.get('/suppliers?supplier_type=TRANSPORT');
      setSuppliers(response.data);
    } catch (error) {
      console.error('Failed to load suppliers');
    }
  };

  const fetchVehicleTypes = async () => {
    try {
      // Fetch from Vehicle Master API
      const response = await api.get('/vehicle-types');
      if (response.data && response.data.length > 0) {
        const vehicleObjs = response.data.map(v => ({
          value: v.vehicle_name,
          label: `${v.vehicle_name}`,
          capacity: v.seating_capacity,  // Use seating_capacity (after luggage)
          total_seats: v.total_seats,
          vehicle_type_id: v.vehicle_type_id,
          transport_mode: v.transport_mode || 'Road',  // Include transport mode for filtering
        }));
        setVehicleTypes(vehicleObjs);
      }
    } catch (error) {
      console.error('Failed to load vehicle types from Vehicle Master');
      // Keep default vehicle types as fallback
    }
  };
  
  // Get vehicles filtered by transport mode
  // Note: Existing vehicles may have null transport_mode, treat as "Road"
  const getVehiclesByMode = (mode) => {
    return vehicleTypes.filter(v => {
      const vehicleMode = v.transport_mode || 'Road';
      return vehicleMode === mode;
    });
  };

  // Point-to-Point functions
  const fetchP2pTransfers = async () => {
    try {
      const response = await api.get('/point-to-point/');
      setP2pTransfers(response.data || []);
    } catch (error) {
      console.error('Failed to load P2P transfers');
    }
  };

  const fetchP2pMasters = async () => {
    try {
      const response = await api.get('/hotel-rates/masters');
      if (response.data) {
        setP2pMasters(prev => ({
          ...prev,
          transportModes: response.data.transportModes || prev.transportModes,
          transferCategories: response.data.transferCategories || prev.transferCategories,
          inclusionTypes: response.data.inclusionTypes || prev.inclusionTypes,
          gstSlabs: response.data.gstSlabs || prev.gstSlabs,
          currencies: response.data.currencies || prev.currencies,
        }));
      }
    } catch (error) {
      console.error('Failed to load P2P masters');
    }
  };

  const handleP2pSubmit = async (e) => {
    e.preventDefault();
    
    // Validate at least one tier has a rate
    const validTiers = pricingTiers.filter(t => t.rate && parseFloat(t.rate) > 0);
    if (validTiers.length === 0) {
      toast.error('Please add at least one pricing tier with a rate');
      return;
    }
    
    try {
      if (editingP2p) {
        // When editing, update the single record
        const tier = pricingTiers[0] || {};
        const data = {
          ...p2pForm,
          variant_name: tier.variant_name || '',
          max_pax: parseInt(tier.max_pax) || 4,
          rate: parseFloat(tier.rate) || 0,
          gst_slab: parseInt(p2pForm.gst_slab) || 5,
          cost_rate: tier.cost_rate ? parseFloat(tier.cost_rate) : null,
          inclusion_type: tier.inclusion_type || 'Mandatory',
          valid_from: p2pForm.valid_from || null,
          valid_to: p2pForm.valid_to || null,
          vehicle_type_id: p2pForm.vehicle_type_id || null,
          supplier_id: p2pForm.supplier_id || null,
          from_place_id: p2pForm.from_place_id || null,
          to_place_id: p2pForm.to_place_id || null,
        };
        
        await api.put(`/point-to-point/${editingP2p.p2p_id}`, data);
        toast.success('Leg transfer updated');
      } else {
        // When creating, create one record per tier
        const createdCount = await Promise.all(
          validTiers.map(async (tier) => {
            const data = {
              ...p2pForm,
              variant_name: tier.variant_name || '',
              max_pax: parseInt(tier.max_pax) || 4,
              rate: parseFloat(tier.rate) || 0,
              gst_slab: parseInt(p2pForm.gst_slab) || 5,
              cost_rate: tier.cost_rate ? parseFloat(tier.cost_rate) : null,
              inclusion_type: tier.inclusion_type || 'Mandatory',
              valid_from: p2pForm.valid_from || null,
              valid_to: p2pForm.valid_to || null,
              vehicle_type_id: p2pForm.vehicle_type_id || null,
              supplier_id: p2pForm.supplier_id || null,
              from_place_id: p2pForm.from_place_id || null,
              to_place_id: p2pForm.to_place_id || null,
            };
            return await api.post('/point-to-point/', data);
          })
        );
        toast.success(`Created ${createdCount.length} transfer rate${createdCount.length > 1 ? 's' : ''}`);
      }
      
      setP2pDialogOpen(false);
      resetP2pForm();
      fetchP2pTransfers();
    } catch (error) {
      toast.error('Failed to save leg transfer');
      console.error(error);
    }
  };

  const resetP2pForm = () => {
    setP2pForm({
      // Location
      from_place: '',
      to_place: '',
      from_place_id: '',
      to_place_id: '',
      // Transport
      transport_mode: 'Road',
      vehicle_type: '',
      vehicle_type_id: '',
      // Classification
      transfer_category: 'Intercity',
      description: '',
      // Common Settings
      pricing_type: 'PER_VEHICLE',
      currency: 'INR',
      gst_slab: 5,
      includes_gst: false,
      // Validity
      valid_from: '',
      valid_to: '',
      season_name: '',
      // Supplier
      supplier_id: '',
      // Remarks
      remarks: '',
    });
    // Reset pricing tiers
    setPricingTiers([
      { variant_name: '', rate: '', cost_rate: '', max_pax: 4, inclusion_type: 'Mandatory' }
    ]);
    setEditingP2p(null);
  };

  const handleEditP2p = (p2p) => {
    setEditingP2p(p2p);
    setP2pForm({
      // Location
      from_place: p2p.from_place || '',
      to_place: p2p.to_place || '',
      from_place_id: p2p.from_place_id || '',
      to_place_id: p2p.to_place_id || '',
      // Transport
      transport_mode: p2p.transport_mode || 'Road',
      vehicle_type: p2p.vehicle_type || '',
      vehicle_type_id: p2p.vehicle_type_id || '',
      // Classification
      transfer_category: p2p.transfer_category || 'Intercity',
      description: p2p.description || '',
      // Common Settings
      pricing_type: p2p.pricing_type || 'PER_VEHICLE',
      currency: p2p.currency || 'INR',
      gst_slab: p2p.gst_slab ?? 5,
      includes_gst: p2p.includes_gst || false,
      // Validity
      valid_from: p2p.valid_from ? p2p.valid_from.split('T')[0] : '',
      valid_to: p2p.valid_to ? p2p.valid_to.split('T')[0] : '',
      season_name: p2p.season_name || '',
      // Supplier
      supplier_id: p2p.supplier_id || '',
      // Remarks
      remarks: p2p.remarks || '',
    });
    // Set single tier when editing (existing record has one tier)
    setPricingTiers([{
      variant_name: p2p.variant_name || '',
      rate: p2p.rate || p2p.base_rate || '',
      cost_rate: p2p.cost_rate || '',
      max_pax: p2p.max_pax || 4,
      inclusion_type: p2p.inclusion_type || 'Mandatory',
    }]);
    setP2pDialogOpen(true);
  };

  const handleDeleteP2p = async (id) => {
    try {
      await api.delete(`/point-to-point/${id}`);
      toast.success('Transfer deleted');
      setP2pDeleteConfirm({ open: false, id: null, name: '' });
      fetchP2pTransfers();
    } catch (error) {
      toast.error('Failed to delete transfer');
    }
  };

  const confirmDeleteP2p = (p2p) => {
    setP2pDeleteConfirm({
      open: true,
      id: p2p.p2p_id,
      name: `${p2p.from_place} → ${p2p.to_place}`,
    });
  };

  // Pricing Tier helpers
  const addPricingTier = () => {
    setPricingTiers([...pricingTiers, { variant_name: '', rate: '', cost_rate: '', max_pax: 4, inclusion_type: 'Optional' }]);
  };
  
  const removePricingTier = (index) => {
    if (pricingTiers.length > 1) {
      setPricingTiers(pricingTiers.filter((_, i) => i !== index));
    }
  };
  
  const updatePricingTier = (index, field, value) => {
    const updated = [...pricingTiers];
    updated[index] = { ...updated[index], [field]: value };
    setPricingTiers(updated);
  };

  // P2P Download as Excel via Backend API
  const handleP2pDownload = async () => {
    try {
      if (p2pTransfers.length === 0) {
        toast.error('No transfers to download');
        return;
      }
      
      // Use backend API to generate and download Excel
      const response = await api.get('/point-to-point/download/excel', {
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `p2p_transfers_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Excel file downloaded');
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Failed to download Excel file');
    }
  };

  // P2P Download Sample Template via Backend API
  const handleP2pDownloadTemplate = async () => {
    try {
      // Use backend API to generate and download template
      const response = await api.get('/point-to-point/download/template', {
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'p2p_transfers_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Template downloaded');
    } catch (error) {
      console.error('Template download failed:', error);
      toast.error('Failed to download template');
    }
  };

  // P2P Excel File Upload
  const handleP2pFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setP2pUploading(true);
    
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const arrayBuffer = e.target.result;
          const workbook = new ExcelJS.Workbook();
          await workbook.xlsx.load(arrayBuffer);
          
          // Get first sheet (skip Instructions sheet if present)
          const worksheet =
            workbook.worksheets.find(ws => ws.name.toLowerCase() !== 'instructions') ||
            workbook.worksheets[0];
          
          // Convert to JSON: read header row, then map each subsequent row
          // to an object keyed by header name (matches xlsx.sheet_to_json behaviour).
          const jsonData = [];
          if (worksheet && worksheet.rowCount > 1) {
            const headerRow = worksheet.getRow(1);
            const headers = [];
            headerRow.eachCell({ includeEmpty: false }, (cell, colNumber) => {
              headers[colNumber] = (cell.value ?? '').toString().trim();
            });
            for (let rowNum = 2; rowNum <= worksheet.rowCount; rowNum++) {
              const row = worksheet.getRow(rowNum);
              const obj = {};
              let hasAny = false;
              row.eachCell({ includeEmpty: false }, (cell, colNumber) => {
                const key = headers[colNumber];
                if (!key) return;
                let v = cell.value;
                // Normalise rich text / hyperlink / date objects to plain values
                if (v && typeof v === 'object') {
                  if (v.text !== undefined) v = v.text;
                  else if (v.result !== undefined) v = v.result;
                  else if (v instanceof Date) v = v.toISOString().split('T')[0];
                }
                obj[key] = v;
                hasAny = true;
              });
              if (hasAny) jsonData.push(obj);
            }
          }
          
          if (jsonData.length === 0) {
            toast.error('No data found in Excel file');
            setP2pUploading(false);
            return;
          }
          
          // Map Excel columns to API fields (handles both old and new formats)
          const items = jsonData.map(row => {
            // Helper to get value from multiple possible column names
            const getValue = (...keys) => {
              for (const key of keys) {
                if (row[key] !== undefined && row[key] !== null && row[key] !== '') {
                  return row[key];
                }
              }
              return null;
            };
            
            // Parse GST Included field
            const gstIncludedRaw = getValue('GST Included', 'gst_included', 'Includes GST', 'includes_gst');
            const gstIncluded = gstIncludedRaw ? 
              (String(gstIncludedRaw).toLowerCase() === 'yes' || gstIncludedRaw === true || gstIncludedRaw === 'true') : false;
            
            return {
              from_place: getValue('From Place', 'from_place', 'From', 'Origin') || '',
              to_place: getValue('To Place', 'to_place', 'To', 'Destination') || '',
              transport_mode: getValue('Transport Mode', 'transport_mode', 'Mode') || 'Road',
              vehicle_type: getValue('Vehicle Type', 'vehicle_type', 'Vehicle') || 'Car',
              variant_name: getValue('Variant Name', 'variant_name', 'Variant', 'Tier') || '',
              transfer_category: getValue('Transfer Category', 'transfer_category', 'Category', 'Transfer Type', 'transfer_type') || 'Intercity',
              description: getValue('Description', 'description', 'Desc') || '',
              pricing_type: getValue('Pricing Type', 'pricing_type', 'Price Type') || 'PER_VEHICLE',
              max_pax: parseInt(getValue('Max Pax', 'max_pax', 'Capacity', 'Pax')) || 4,
              rate: parseFloat(getValue('Sell Rate', 'Rate', 'rate', 'Sell Price', 'Price')) || 0,
              cost_rate: parseFloat(getValue('Cost Rate', 'cost_rate', 'Cost', 'Cost Price')) || null,
              currency: getValue('Currency', 'currency') || 'INR',
              gst_slab: parseInt(getValue('GST %', 'GST', 'gst_slab', 'GST Slab')) || 5,
              includes_gst: gstIncluded,
              inclusion_type: getValue('Inclusion Type', 'inclusion_type', 'Show As', 'show_as', 'Inclusion') || 'Mandatory',
              valid_from: getValue('Valid From', 'valid_from', 'Start Date') || '',
              valid_to: getValue('Valid To', 'valid_to', 'End Date') || '',
              season_name: getValue('Season Name', 'season_name', 'Season') || '',
              remarks: getValue('Remarks', 'remarks', 'Notes') || '',
            };
          }).filter(item => item.from_place && item.to_place && item.rate > 0);
          
          if (items.length === 0) {
            toast.error('No valid rows found. Ensure From Place, To Place, and Sell Rate are filled.');
            setP2pUploading(false);
            return;
          }
          
          // Send bulk import request
          const response = await api.post('/point-to-point/bulk-import', { items });
          
          const { success_count, error_count, errors } = response.data;
          
          if (success_count > 0) {
            toast.success(`Imported ${success_count} transfers${error_count > 0 ? `, ${error_count} failed` : ''}`);
          }
          
          if (error_count > 0 && errors.length > 0) {
            // Show first 3 errors
            const errorMessages = errors.slice(0, 3).map(e => 
              `Row ${e.row}: ${e.from_place} → ${e.to_place}: ${e.error}`
            ).join('\n');
            toast.error(`Import errors:\n${errorMessages}${errors.length > 3 ? `\n...and ${errors.length - 3} more` : ''}`);
          }
          
          setP2pBulkDialogOpen(false);
          fetchP2pTransfers();
        } catch (parseError) {
          console.error('Parse error:', parseError);
          toast.error('Failed to parse Excel file: ' + (parseError.response?.data?.detail || parseError.message));
        }
        setP2pUploading(false);
      };
      
      reader.onerror = () => {
        toast.error('Failed to read file');
        setP2pUploading(false);
      };
      
      reader.readAsArrayBuffer(file);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Upload failed');
      setP2pUploading(false);
    }
    
    // Reset file input
    if (p2pFileInputRef.current) {
      p2pFileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Auto-fill vehicle_label from vehicle_type if empty
      const vehicle_label = formData.vehicle_label?.trim() || formData.vehicle_type;
      
      const data = {
        ...formData,
        vehicle_label,
        max_pax_capacity: parseInt(formData.max_pax_capacity),
        rate: parseFloat(formData.rate),
        gst_slab: parseInt(formData.gst_slab),
        arrival_city: formData.transfer_type === 'CITY_TO_CITY' ? formData.arrival_city : null,
        departure_city: formData.transfer_type === 'CITY_TO_CITY' ? formData.departure_city : null,
        route_id: formData.transfer_type === 'ROUTE_BASED' ? formData.route_id : null,
        supplier_id: formData.supplier_id || null,
        contract_type: formData.transfer_type === 'ROUTE_BASED' ? 'PER_ROUTE' : 'PER_DAY',
      };
      
      if (editingTransfer) {
        await api.put(`/transfers/${editingTransfer.transfer_rate_id}`, data);
        toast.success('Transfer rate updated successfully');
      } else {
        await api.post('/transfers', data);
        toast.success('Transfer rate created successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchTransfers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save transfer rate');
    }
  };

  const handleBulkSubmit = async (e) => {
    e.preventDefault();
    const selectedVehicles = bulkData.vehicles.filter(v => v.selected && v.rate);
    
    if (selectedVehicles.length === 0) {
      toast.error('Please select at least one vehicle and enter its rate');
      return;
    }

    try {
      const data = {
        transfer_type: bulkData.transfer_type,
        arrival_city: bulkData.transfer_type === 'CITY_TO_CITY' ? bulkData.arrival_city : null,
        departure_city: bulkData.transfer_type === 'CITY_TO_CITY' ? bulkData.departure_city : null,
        route_id: bulkData.transfer_type === 'ROUTE_BASED' ? bulkData.route_id : null,
        supplier_id: bulkData.supplier_id || null,
        date_from: bulkData.date_from,
        date_to: bulkData.date_to,
        gst_slab: parseInt(bulkData.gst_slab),
        vehicles: selectedVehicles.map(v => ({
          vehicle_type: v.vehicle_type,
          vehicle_label: v.vehicle_label,
          max_pax_capacity: v.max_pax_capacity,
          rate: parseFloat(v.rate),
        })),
      };
      
      const response = await api.post('/transfers/bulk', data);
      toast.success(`Created ${response.data.count} transfer rates`);
      setBulkDialogOpen(false);
      resetBulkForm();
      fetchTransfers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create transfer rates');
    }
  };

  const handleDelete = async (transferId) => {
    if (!window.confirm('Are you sure you want to delete this transfer rate?')) return;
    try {
      await api.delete(`/transfers/${transferId}`);
      toast.success('Transfer rate deleted successfully');
      fetchTransfers();
    } catch (error) {
      toast.error('Failed to delete transfer rate');
    }
  };

  const resetForm = () => {
    const defaultVehicle = vehicleTypes[0]?.value || '';
    setFormData({
      transfer_type: activeTab === 'city' ? 'CITY_TO_CITY' : 'ROUTE_BASED',
      arrival_city: '',
      departure_city: '',
      route_id: '',
      supplier_id: '',
      vehicle_type: defaultVehicle,
      vehicle_label: '',
      max_pax_capacity: '',
      rate: '',
      gst_slab: 5,
      contract_type: activeTab === 'city' ? 'PER_DAY' : 'PER_ROUTE',
      date_from: '',
      date_to: '',
    });
    setEditingTransfer(null);
  };

  const resetBulkForm = () => {
    setBulkData({
      transfer_type: activeTab === 'city' ? 'CITY_TO_CITY' : 'ROUTE_BASED',
      arrival_city: '',
      departure_city: '',
      route_id: '',
      supplier_id: '',
      date_from: '',
      date_to: '',
      gst_slab: 5,
      vehicles: vehicleTypes.map(v => ({
        vehicle_type: v.value,
        vehicle_label: `${v.label} (${v.capacity} pax)`,
        max_pax_capacity: v.capacity,
        rate: '',
        selected: false,
      })),
    });
  };

  const handleEdit = (transfer) => {
    setEditingTransfer(transfer);
    setFormData({
      transfer_type: transfer.transfer_type || 'CITY_TO_CITY',
      arrival_city: transfer.arrival_city || '',
      departure_city: transfer.departure_city || '',
      route_id: transfer.route_id || '',
      supplier_id: transfer.supplier_id || '',
      vehicle_type: transfer.vehicle_type,
      vehicle_label: transfer.vehicle_label,
      max_pax_capacity: transfer.max_pax_capacity?.toString(),
      rate: (transfer.rate || transfer.rate_per_day)?.toString(),
      gst_slab: transfer.gst_slab || 5,
      contract_type: transfer.contract_type || 'PER_DAY',
      date_from: transfer.date_from,
      date_to: transfer.date_to,
    });
    setDialogOpen(true);
  };

  const handleVehicleTypeChange = (value) => {
    const selectedType = vehicleTypes.find(v => v.value === value);
    const newCapacity = selectedType?.capacity || 4;
    const newLabel = `${selectedType?.label || value} (${newCapacity} pax)`;
    
    setFormData({
      ...formData,
      vehicle_type: value,
      vehicle_label: newLabel,  // Always update label to match vehicle type
      max_pax_capacity: newCapacity.toString(),
    });
  };

  const openAddDialog = (type) => {
    const defaultVehicle = vehicleTypes[0]?.value || '';
    setFormData({
      transfer_type: type,
      arrival_city: '',
      departure_city: '',
      route_id: '',
      supplier_id: '',
      vehicle_type: defaultVehicle,
      vehicle_label: '',
      max_pax_capacity: '',
      rate: '',
      gst_slab: 5,
      contract_type: type === 'CITY_TO_CITY' ? 'PER_DAY' : 'PER_ROUTE',
      date_from: '',
      date_to: '',
    });
    setEditingTransfer(null);
    setDialogOpen(true);
  };

  const openBulkDialog = (type) => {
    setBulkData({
      ...bulkData,
      transfer_type: type,
      arrival_city: '',
      departure_city: '',
      route_id: '',
    });
    setBulkDialogOpen(true);
  };

  const updateBulkVehicle = (index, field, value) => {
    const newVehicles = [...bulkData.vehicles];
    newVehicles[index] = { ...newVehicles[index], [field]: value };
    setBulkData({ ...bulkData, vehicles: newVehicles });
  };

  const applyFilters = () => {
    fetchTransfers();
  };

  const clearFilters = () => {
    setFilters({ supplier_id: '', vehicle_type: '', arrival_city: '' });
    setTimeout(fetchTransfers, 100);
  };

  const cityTransfers = transfers.filter(t => t.transfer_type === 'CITY_TO_CITY' || !t.transfer_type);
  const routeTransfers = transfers.filter(t => t.transfer_type === 'ROUTE_BASED');

  const filteredCityTransfers = cityTransfers.filter(t =>
    t.vehicle_label?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.arrival_city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.supplier_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredRouteTransfers = routeTransfers.filter(t =>
    t.vehicle_label?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.route_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.supplier_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get unique cities from transfers for filter
  const uniqueCities = [...new Set(cityTransfers.map(t => t.arrival_city).filter(Boolean))];

  return (
    <Layout>
      <div className="space-y-6" data-testid="transfer-manager-page">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Transfer Manager</h1>
            <p className="text-sm sm:text-base text-gray-600 mt-1">
              {cityTransfers.length} city rates, {routeTransfers.length} route rates
            </p>
          </div>
          <Button variant="outline" onClick={() => setShowFilters(!showFilters)}>
            <Filter className="w-4 h-4 mr-2" />
            Filters
          </Button>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <Card>
            <CardContent className="pt-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>Supplier</Label>
                  <Select value={filters.supplier_id || "all"} onValueChange={(v) => setFilters({ ...filters, supplier_id: v === "all" ? "" : v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="All suppliers" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All suppliers</SelectItem>
                      {suppliers.map((s) => (
                        <SelectItem key={s.supplier_id} value={s.supplier_id}>{s.supplier_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Vehicle Type</Label>
                  <Select value={filters.vehicle_type || "all"} onValueChange={(v) => setFilters({ ...filters, vehicle_type: v === "all" ? "" : v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="All vehicles" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All vehicles</SelectItem>
                      {vehicleTypes.map((v) => (
                        <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>City</Label>
                  <Select value={filters.arrival_city || "all"} onValueChange={(v) => setFilters({ ...filters, arrival_city: v === "all" ? "" : v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="All cities" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All cities</SelectItem>
                      {uniqueCities.map((city) => (
                        <SelectItem key={city} value={city}>{city}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end gap-2">
                  <Button onClick={applyFilters} className="flex-1">Apply</Button>
                  <Button variant="outline" onClick={clearFilters}>Clear</Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3 max-w-lg">
            <TabsTrigger value="city" className="flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              City to City
            </TabsTrigger>
            <TabsTrigger value="route" className="flex items-center gap-2">
              <Route className="w-4 h-4" />
              Route Based
            </TabsTrigger>
            <TabsTrigger value="p2p" className="flex items-center gap-2">
              <Car className="w-4 h-4" />
              Point to Point
            </TabsTrigger>
          </TabsList>

          {/* City to City Tab */}
          <TabsContent value="city" className="mt-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex flex-col sm:flex-row gap-4 mb-4 justify-between">
                  <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search city transfers..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => openBulkDialog('CITY_TO_CITY')}>
                      <Plus className="w-4 h-4 mr-2" />
                      Bulk Add
                    </Button>
                    <Button onClick={() => openAddDialog('CITY_TO_CITY')} data-testid="add-city-transfer-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Single
                    </Button>
                  </div>
                </div>

                {loading ? (
                  <div className="text-center py-8">Loading...</div>
                ) : filteredCityTransfers.length === 0 ? (
                  <div className="text-center py-8 text-gray-600">No city-to-city transfers found</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-gray-50">
                          <th className="text-left py-3 px-2 font-medium text-gray-700">Arrival</th>
                          <th className="text-left py-3 px-2 font-medium text-gray-700">Departure</th>
                          <th className="text-left py-3 px-2 font-medium text-gray-700">Supplier</th>
                          <th className="text-left py-3 px-2 font-medium text-gray-700">Vehicle</th>
                          <th className="text-center py-3 px-2 font-medium text-gray-700">Cap</th>
                          <th className="text-right py-3 px-2 font-medium text-gray-700">Rate/Day</th>
                          <th className="text-center py-3 px-2 font-medium text-gray-700">GST</th>
                          <th className="text-left py-3 px-2 font-medium text-gray-700">Validity</th>
                          <th className="text-center py-3 px-2 font-medium text-gray-700">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredCityTransfers.map((transfer) => (
                          <tr key={transfer.transfer_rate_id} className="border-b hover:bg-gray-50">
                            <td className="py-3 px-2 font-medium">{transfer.arrival_city || '-'}</td>
                            <td className="py-3 px-2">{transfer.departure_city || '-'}</td>
                            <td className="py-3 px-2 text-xs text-gray-600">{transfer.supplier_name || '-'}</td>
                            <td className="py-3 px-2">
                              <div className="flex items-center gap-1">
                                <Car className="w-3 h-3 text-gray-400" />
                                <span className="text-xs">{transfer.vehicle_label}</span>
                              </div>
                            </td>
                            <td className="py-3 px-2 text-center text-xs">{transfer.max_pax_capacity}</td>
                            <td className="py-3 px-2 text-right font-medium text-green-600">
                              ₹{(transfer.rate || transfer.rate_per_day)?.toLocaleString()}
                            </td>
                            <td className="py-3 px-2 text-center text-xs">{transfer.gst_slab || 5}%</td>
                            <td className="py-3 px-2 text-xs text-gray-500">
                              {transfer.date_from} to {transfer.date_to}
                            </td>
                            <td className="py-3 px-2">
                              <div className="flex justify-center gap-1">
                                <Button variant="ghost" size="sm" onClick={() => handleEdit(transfer)}>
                                  <Edit className="w-3 h-3" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => handleDelete(transfer.transfer_rate_id)}>
                                  <Trash2 className="w-3 h-3 text-red-500" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Route Based Tab */}
          <TabsContent value="route" className="mt-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex flex-col sm:flex-row gap-4 mb-4 justify-between">
                  <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search route transfers..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => openBulkDialog('ROUTE_BASED')}>
                      <Plus className="w-4 h-4 mr-2" />
                      Bulk Add
                    </Button>
                    <Button onClick={() => openAddDialog('ROUTE_BASED')} data-testid="add-route-transfer-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Single
                    </Button>
                  </div>
                </div>

                {loading ? (
                  <div className="text-center py-8">Loading...</div>
                ) : filteredRouteTransfers.length === 0 ? (
                  <div className="text-center py-8 text-gray-600">No route-based transfers found</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-gray-50">
                          <th className="text-left py-3 px-2 font-medium text-gray-700">Route</th>
                          <th className="text-left py-3 px-2 font-medium text-gray-700">Supplier</th>
                          <th className="text-left py-3 px-2 font-medium text-gray-700">Vehicle</th>
                          <th className="text-center py-3 px-2 font-medium text-gray-700">Cap</th>
                          <th className="text-right py-3 px-2 font-medium text-gray-700">Rate/Route</th>
                          <th className="text-center py-3 px-2 font-medium text-gray-700">GST</th>
                          <th className="text-left py-3 px-2 font-medium text-gray-700">Validity</th>
                          <th className="text-center py-3 px-2 font-medium text-gray-700">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredRouteTransfers.map((transfer) => (
                          <tr key={transfer.transfer_rate_id} className="border-b hover:bg-gray-50">
                            <td className="py-3 px-2">
                              <div className="font-medium">{transfer.route_name || 'Unknown'}</div>
                              <div className="text-xs text-gray-500">{transfer.route_code}</div>
                            </td>
                            <td className="py-3 px-2 text-xs text-gray-600">{transfer.supplier_name || '-'}</td>
                            <td className="py-3 px-2">
                              <div className="flex items-center gap-1">
                                <Car className="w-3 h-3 text-gray-400" />
                                <span className="text-xs">{transfer.vehicle_label}</span>
                              </div>
                            </td>
                            <td className="py-3 px-2 text-center text-xs">{transfer.max_pax_capacity}</td>
                            <td className="py-3 px-2 text-right font-medium text-green-600">
                              ₹{(transfer.rate || transfer.rate_per_day)?.toLocaleString()}
                            </td>
                            <td className="py-3 px-2 text-center text-xs">{transfer.gst_slab || 5}%</td>
                            <td className="py-3 px-2 text-xs text-gray-500">
                              {transfer.date_from} to {transfer.date_to}
                            </td>
                            <td className="py-3 px-2">
                              <div className="flex justify-center gap-1">
                                <Button variant="ghost" size="sm" onClick={() => handleEdit(transfer)}>
                                  <Edit className="w-3 h-3" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => handleDelete(transfer.transfer_rate_id)}>
                                  <Trash2 className="w-3 h-3 text-red-500" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Point to Point Tab */}
          <TabsContent value="p2p" className="mt-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex flex-col sm:flex-row gap-4 mb-4 justify-between">
                  <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search point-to-point transfers..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Button variant="outline" onClick={handleP2pDownload} data-testid="download-p2p-btn">
                      <Download className="w-4 h-4 mr-2" />
                      Download Excel
                    </Button>
                    <Button variant="outline" onClick={() => setP2pBulkDialogOpen(true)} data-testid="upload-p2p-btn">
                      <Upload className="w-4 h-4 mr-2" />
                      Bulk Upload
                    </Button>
                    <Button onClick={() => { resetP2pForm(); setP2pDialogOpen(true); }} data-testid="add-p2p-transfer-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Add P2P Transfer
                    </Button>
                  </div>
                </div>

                {p2pTransfers.length === 0 ? (
                  <div className="text-center py-8 text-gray-600">No leg transfers found. Add your first transfer to get started.</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-gray-50">
                          <th className="text-left py-3 px-2 font-medium text-gray-700">Route</th>
                          <th className="text-left py-3 px-2 font-medium text-gray-700">Mode</th>
                          <th className="text-left py-3 px-2 font-medium text-gray-700">Category</th>
                          <th className="text-left py-3 px-2 font-medium text-gray-700">Vehicle</th>
                          <th className="text-center py-3 px-2 font-medium text-gray-700">Pax</th>
                          <th className="text-right py-3 px-2 font-medium text-gray-700">Rate</th>
                          <th className="text-center py-3 px-2 font-medium text-gray-700">Validity</th>
                          <th className="text-center py-3 px-2 font-medium text-gray-700">Include</th>
                          <th className="text-center py-3 px-2 font-medium text-gray-700">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {p2pTransfers.filter(t =>
                          t.from_place?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          t.to_place?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          t.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          t.variant_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          t.transfer_category?.toLowerCase().includes(searchTerm.toLowerCase())
                        ).map((p2p) => (
                          <tr key={p2p.p2p_id} className="border-b hover:bg-gray-50">
                            <td className="py-3 px-2">
                              <div className="font-medium">{p2p.from_place}</div>
                              <div className="text-xs text-gray-500">→ {p2p.to_place}</div>
                              {p2p.description && <div className="text-xs text-gray-400 truncate max-w-[150px]" title={p2p.description}>{p2p.description}</div>}
                            </td>
                            <td className="py-3 px-2">
                              <span className={`px-2 py-0.5 rounded text-xs ${
                                p2p.transport_mode === 'Road' ? 'bg-blue-100 text-blue-700' :
                                p2p.transport_mode === 'Ferry' ? 'bg-cyan-100 text-cyan-700' :
                                p2p.transport_mode === 'Train' ? 'bg-purple-100 text-purple-700' :
                                p2p.transport_mode === 'Flight' ? 'bg-indigo-100 text-indigo-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {p2p.transport_mode || 'Road'}
                              </span>
                            </td>
                            <td className="py-3 px-2 text-xs">{p2p.transfer_category || p2p.transfer_type}</td>
                            <td className="py-3 px-2">
                              <div className="text-xs font-medium">{p2p.vehicle_type}</div>
                              {p2p.variant_name && <div className="text-xs text-gray-500">{p2p.variant_name}</div>}
                            </td>
                            <td className="py-3 px-2 text-center">{p2p.max_pax}</td>
                            <td className="py-3 px-2 text-right">
                              <div className="font-medium text-green-600">
                                {p2p.currency === 'INR' ? '₹' : p2p.currency} {(p2p.base_rate || p2p.rate)?.toLocaleString()}
                              </div>
                              <div className="text-xs text-gray-500">
                                {p2p.pricing_type === 'PER_PERSON' ? '/person' : '/vehicle'}
                                {p2p.gst_slab > 0 && <span className="ml-1">+{p2p.gst_slab}% GST</span>}
                              </div>
                            </td>
                            <td className="py-3 px-2 text-center text-xs">
                              {p2p.valid_from && p2p.valid_to && p2p.valid_from !== '2020-01-01' ? (
                                <div>
                                  <div>{new Date(p2p.valid_from).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</div>
                                  <div className="text-gray-400">to {new Date(p2p.valid_to).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</div>
                                  {p2p.season_name && <div className="text-xs text-orange-600">{p2p.season_name}</div>}
                                </div>
                              ) : (
                                <span className="text-gray-400">Always</span>
                              )}
                            </td>
                            <td className="py-3 px-2 text-center">
                              <span className={`px-2 py-1 rounded text-xs ${
                                (p2p.inclusion_type || p2p.show_as) === 'Mandatory' ? 'bg-green-100 text-green-700' :
                                (p2p.inclusion_type || p2p.show_as) === 'Optional' ? 'bg-blue-100 text-blue-700' :
                                'bg-orange-100 text-orange-700'
                              }`}>
                                {p2p.inclusion_type || p2p.show_as}
                              </span>
                            </td>
                            <td className="py-3 px-2">
                              <div className="flex justify-center gap-1">
                                <Button variant="ghost" size="sm" onClick={() => handleEditP2p(p2p)} data-testid={`edit-p2p-${p2p.p2p_id}`}>
                                  <Edit className="w-3 h-3" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => confirmDeleteP2p(p2p)} data-testid={`delete-p2p-${p2p.p2p_id}`}>
                                  <Trash2 className="w-3 h-3 text-red-500" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Single Add/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {editingTransfer ? 'Edit Transfer Rate' : 
                  formData.transfer_type === 'CITY_TO_CITY' ? 'Add City Transfer' : 'Add Route Transfer'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {formData.transfer_type === 'CITY_TO_CITY' ? (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Arrival City *</Label>
                    <SearchableSelect
                      options={places.map(p => ({ value: p.place_name, label: p.place_name }))}
                      value={formData.arrival_city || ""}
                      onValueChange={(v) => setFormData({ ...formData, arrival_city: v })}
                      placeholder="Select city"
                      searchPlaceholder="Search cities..."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Departure City *</Label>
                    <SearchableSelect
                      options={places.map(p => ({ value: p.place_name, label: p.place_name }))}
                      value={formData.departure_city || ""}
                      onValueChange={(v) => setFormData({ ...formData, departure_city: v })}
                      placeholder="Select city"
                      searchPlaceholder="Search cities..."
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>Predefined Route *</Label>
                  <Select value={formData.route_id || "select"} onValueChange={(v) => setFormData({ ...formData, route_id: v === "select" ? "" : v })}>
                    <SelectTrigger><SelectValue placeholder="Select route" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="select">Select route</SelectItem>
                      {routes.map((r) => <SelectItem key={r.route_id} value={r.route_id}>{r.route_code} - {r.route_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              <div className="space-y-2">
                <Label>Supplier</Label>
                <Select value={formData.supplier_id || "none"} onValueChange={(v) => setFormData({ ...formData, supplier_id: v === "none" ? "" : v })}>
                  <SelectTrigger><SelectValue placeholder="Select supplier" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No supplier</SelectItem>
                    {suppliers.map((s) => <SelectItem key={s.supplier_id} value={s.supplier_id}>{s.supplier_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Vehicle Type *</Label>
                  <Select value={formData.vehicle_type} onValueChange={handleVehicleTypeChange}>
                    <SelectTrigger><SelectValue placeholder="Select vehicle type" /></SelectTrigger>
                    <SelectContent className="max-h-60 overflow-y-auto">
                      {vehicleTypes.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Max Capacity *</Label>
                  <Input type="number" value={formData.max_pax_capacity} onChange={(e) => setFormData({ ...formData, max_pax_capacity: e.target.value })} required />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Vehicle Label</Label>
                <Input 
                  value={formData.vehicle_label} 
                  onChange={(e) => setFormData({ ...formData, vehicle_label: e.target.value })} 
                  placeholder={formData.vehicle_type || "Auto-generated from vehicle type"}
                />
                <p className="text-xs text-gray-500">Leave empty to use vehicle type as label</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Rate (₹) *</Label>
                  <Input type="number" value={formData.rate} onChange={(e) => setFormData({ ...formData, rate: e.target.value })} required />
                </div>
                <div className="space-y-2">
                  <Label>GST Slab</Label>
                  <Select value={formData.gst_slab?.toString()} onValueChange={(v) => setFormData({ ...formData, gst_slab: parseInt(v) })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {GST_SLABS.map((g) => <SelectItem key={g.value} value={g.value.toString()}>{g.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Valid From *</Label>
                  <Input type="date" value={formData.date_from} onChange={(e) => setFormData({ ...formData, date_from: e.target.value })} required />
                </div>
                <div className="space-y-2">
                  <Label>Valid To *</Label>
                  <Input type="date" value={formData.date_to} onChange={(e) => setFormData({ ...formData, date_to: e.target.value })} required />
                </div>
              </div>
              
              <div className="flex gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">Cancel</Button>
                <Button type="submit" className="flex-1">{editingTransfer ? 'Update' : 'Create'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Bulk Add Dialog */}
        <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                Bulk Add {bulkData.transfer_type === 'CITY_TO_CITY' ? 'City' : 'Route'} Transfers
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleBulkSubmit} className="space-y-4">
              {bulkData.transfer_type === 'CITY_TO_CITY' ? (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Arrival City *</Label>
                    <SearchableSelect
                      options={places.map(p => ({ value: p.place_name, label: p.place_name }))}
                      value={bulkData.arrival_city || ""}
                      onValueChange={(v) => setBulkData({ ...bulkData, arrival_city: v })}
                      placeholder="Select city"
                      searchPlaceholder="Search cities..."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Departure City *</Label>
                    <SearchableSelect
                      options={places.map(p => ({ value: p.place_name, label: p.place_name }))}
                      value={bulkData.departure_city || ""}
                      onValueChange={(v) => setBulkData({ ...bulkData, departure_city: v })}
                      placeholder="Select city"
                      searchPlaceholder="Search cities..."
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>Predefined Route *</Label>
                  <Select value={bulkData.route_id || "select"} onValueChange={(v) => setBulkData({ ...bulkData, route_id: v === "select" ? "" : v })}>
                    <SelectTrigger><SelectValue placeholder="Select route" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="select">Select route</SelectItem>
                      {routes.map((r) => <SelectItem key={r.route_id} value={r.route_id}>{r.route_code} - {r.route_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Supplier</Label>
                  <Select value={bulkData.supplier_id || "none"} onValueChange={(v) => setBulkData({ ...bulkData, supplier_id: v === "none" ? "" : v })}>
                    <SelectTrigger><SelectValue placeholder="Select supplier" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No supplier</SelectItem>
                      {suppliers.map((s) => <SelectItem key={s.supplier_id} value={s.supplier_id}>{s.supplier_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Valid From *</Label>
                  <Input type="date" value={bulkData.date_from} onChange={(e) => setBulkData({ ...bulkData, date_from: e.target.value })} required />
                </div>
                <div className="space-y-2">
                  <Label>Valid To *</Label>
                  <Input type="date" value={bulkData.date_to} onChange={(e) => setBulkData({ ...bulkData, date_to: e.target.value })} required />
                </div>
              </div>

              <div className="space-y-2">
                <Label>GST Slab</Label>
                <Select value={bulkData.gst_slab?.toString()} onValueChange={(v) => setBulkData({ ...bulkData, gst_slab: parseInt(v) })}>
                  <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {GST_SLABS.map((g) => <SelectItem key={g.value} value={g.value.toString()}>{g.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left py-2 px-3 w-10 text-gray-700">Select</th>
                      <th className="text-left py-2 px-3 text-gray-700">Vehicle Type</th>
                      <th className="text-left py-2 px-3 text-gray-700">Vehicle Label</th>
                      <th className="text-center py-2 px-3 w-20 text-gray-700">Capacity</th>
                      <th className="text-right py-2 px-3 w-28 text-gray-700">Rate (₹)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bulkData.vehicles.map((vehicle, index) => (
                      <tr key={vehicle.vehicle_type} className="border-t">
                        <td className="py-2 px-3">
                          <Checkbox
                            checked={vehicle.selected}
                            onCheckedChange={(checked) => updateBulkVehicle(index, 'selected', checked)}
                          />
                        </td>
                        <td className="py-2 px-3 text-gray-600 text-xs">{vehicle.vehicle_type}</td>
                        <td className="py-2 px-3">
                          <Input
                            type="text"
                            placeholder="e.g. Premium Sedan"
                            value={vehicle.vehicle_label}
                            onChange={(e) => updateBulkVehicle(index, 'vehicle_label', e.target.value)}
                            className="w-full text-sm"
                            disabled={!vehicle.selected}
                            data-testid={`bulk-vehicle-label-${index}`}
                          />
                        </td>
                        <td className="py-2 px-3 text-center">
                          <Input
                            type="number"
                            value={vehicle.max_pax_capacity}
                            onChange={(e) => updateBulkVehicle(index, 'max_pax_capacity', parseInt(e.target.value) || 0)}
                            className="w-16 text-center"
                            min="1"
                            disabled={!vehicle.selected}
                          />
                        </td>
                        <td className="py-2 px-3">
                          <Input
                            type="number"
                            placeholder="Enter rate"
                            value={vehicle.rate}
                            onChange={(e) => updateBulkVehicle(index, 'rate', e.target.value)}
                            className="w-full text-right"
                            disabled={!vehicle.selected}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              <div className="flex gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setBulkDialogOpen(false)} className="flex-1">Cancel</Button>
                <Button type="submit" className="flex-1">Create Selected</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Point-to-Point Add/Edit Dialog */}
        <Dialog open={p2pDialogOpen} onOpenChange={setP2pDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingP2p ? 'Edit Leg Transfer' : 'Add Leg Transfer'}</DialogTitle>
              <DialogDescription>
                Configure point-to-point transfer rates for specific route legs
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleP2pSubmit} className="space-y-6">
              {/* ROUTE Section */}
              <div className="space-y-4">
                <h4 className="text-sm font-medium text-slate-700 flex items-center gap-2">
                  <MapPin className="w-4 h-4" /> Route
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>From Place *</Label>
                    <SearchableSelect
                      options={places.map(p => ({ value: p.place_name, label: p.place_name, id: p.place_id }))}
                      value={p2pForm.from_place}
                      onValueChange={(v, option) => setP2pForm({ 
                        ...p2pForm, 
                        from_place: v,
                        from_place_id: option?.id || ''
                      })}
                      placeholder="Select origin"
                      searchPlaceholder="Search places..."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>To Place *</Label>
                    <SearchableSelect
                      options={places.map(p => ({ value: p.place_name, label: p.place_name, id: p.place_id }))}
                      value={p2pForm.to_place}
                      onValueChange={(v, option) => setP2pForm({ 
                        ...p2pForm, 
                        to_place: v,
                        to_place_id: option?.id || ''
                      })}
                      placeholder="Select destination"
                      searchPlaceholder="Search places..."
                    />
                  </div>
                </div>
              </div>

              {/* TRANSPORT Section */}
              <div className="space-y-4">
                <h4 className="text-sm font-medium text-slate-700 flex items-center gap-2">
                  <Car className="w-4 h-4" /> Transport
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Mode *</Label>
                    <Select value={p2pForm.transport_mode} onValueChange={(v) => setP2pForm({ ...p2pForm, transport_mode: v })}>
                      <SelectTrigger><SelectValue placeholder="Select mode" /></SelectTrigger>
                      <SelectContent>
                        {p2pMasters.transportModes.map(mode => (
                          <SelectItem key={mode} value={mode}>{mode}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Category *</Label>
                    <Select value={p2pForm.transfer_category} onValueChange={(v) => setP2pForm({ ...p2pForm, transfer_category: v })}>
                      <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                      <SelectContent>
                        {p2pMasters.transferCategories.map(cat => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Vehicle/Operator *</Label>
                    <Select 
                      value={p2pForm.vehicle_type} 
                      onValueChange={(v) => {
                        const selected = vehicleTypes.find(vt => vt.value === v);
                        setP2pForm({ 
                          ...p2pForm, 
                          vehicle_type: v,
                          vehicle_type_id: selected?.vehicle_type_id || '',
                        });
                        // Update max_pax for all tiers if vehicle has capacity
                        if (selected?.capacity) {
                          setPricingTiers(pricingTiers.map(t => ({ ...t, max_pax: selected.capacity })));
                        }
                      }}
                    >
                      <SelectTrigger><SelectValue placeholder="Select vehicle/operator" /></SelectTrigger>
                      <SelectContent>
                        {getVehiclesByMode(p2pForm.transport_mode).map(vt => (
                          <SelectItem key={vt.value} value={vt.value}>
                            {vt.label} ({vt.capacity || '-'} pax)
                          </SelectItem>
                        ))}
                        {getVehiclesByMode(p2pForm.transport_mode).length === 0 && (
                          <div className="px-2 py-3 text-center text-sm text-slate-500">
                            No {p2pForm.transport_mode} vehicles found.
                            <br />Add in Vehicle Master first.
                          </div>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Input
                    value={p2pForm.description}
                    onChange={(e) => setP2pForm({ ...p2pForm, description: e.target.value })}
                    placeholder="e.g., Airport pickup to hotel - 4 hrs drive"
                  />
                </div>
              </div>

              {/* PRICING TIERS Section */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium text-slate-700 flex items-center gap-2">
                    <IndianRupee className="w-4 h-4" /> Pricing Tiers
                  </h4>
                  {!editingP2p && (
                    <Button type="button" variant="outline" size="sm" onClick={addPricingTier}>
                      <Plus className="w-3 h-3 mr-1" /> Add Tier
                    </Button>
                  )}
                </div>
                
                {/* Common pricing settings */}
                <div className="grid grid-cols-4 gap-4 p-3 bg-slate-50 rounded-lg">
                  <div className="space-y-1">
                    <Label className="text-xs">Pricing Type</Label>
                    <Select value={p2pForm.pricing_type} onValueChange={(v) => setP2pForm({ ...p2pForm, pricing_type: v })}>
                      <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="PER_VEHICLE">Per Vehicle</SelectItem>
                        <SelectItem value="PER_PERSON">Per Person</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">GST Slab</Label>
                    <Select value={String(p2pForm.gst_slab)} onValueChange={(v) => setP2pForm({ ...p2pForm, gst_slab: parseInt(v) })}>
                      <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {p2pMasters.gstSlabs.map(g => (
                          <SelectItem key={g} value={g}>{g}%</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">GST Included?</Label>
                    <Select value={p2pForm.includes_gst ? 'yes' : 'no'} onValueChange={(v) => setP2pForm({ ...p2pForm, includes_gst: v === 'yes' })}>
                      <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="no">No (Add)</SelectItem>
                        <SelectItem value="yes">Yes (Incl.)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Currency</Label>
                    <Select value={p2pForm.currency} onValueChange={(v) => setP2pForm({ ...p2pForm, currency: v })}>
                      <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {p2pMasters.currencies.map(c => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Pricing Tiers Table */}
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100 border-b">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-slate-600">Tier/Variant</th>
                        <th className="px-3 py-2 text-right font-medium text-slate-600">Sell Rate *</th>
                        <th className="px-3 py-2 text-right font-medium text-slate-600">Cost Rate</th>
                        <th className="px-3 py-2 text-center font-medium text-slate-600">Max Pax</th>
                        <th className="px-3 py-2 text-center font-medium text-slate-600">Inclusion</th>
                        <th className="px-3 py-2 w-10"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {pricingTiers.map((tier, idx) => (
                        <tr key={idx} className="hover:bg-slate-50">
                          <td className="px-3 py-2">
                            <Input
                              value={tier.variant_name}
                              onChange={(e) => updatePricingTier(idx, 'variant_name', e.target.value)}
                              placeholder={idx === 0 ? "e.g., Premium" : "e.g., Deluxe"}
                              className="h-8 text-sm"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <Input
                              type="number"
                              value={tier.rate}
                              onChange={(e) => updatePricingTier(idx, 'rate', e.target.value)}
                              placeholder="0"
                              className="h-8 text-sm text-right"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <Input
                              type="number"
                              value={tier.cost_rate}
                              onChange={(e) => updatePricingTier(idx, 'cost_rate', e.target.value)}
                              placeholder="Cost"
                              className="h-8 text-sm text-right"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <Input
                              type="number"
                              value={tier.max_pax}
                              onChange={(e) => updatePricingTier(idx, 'max_pax', e.target.value)}
                              min="1"
                              className="h-8 text-sm text-center w-16 mx-auto"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <Select value={tier.inclusion_type} onValueChange={(v) => updatePricingTier(idx, 'inclusion_type', v)}>
                              <SelectTrigger className="h-8 text-sm w-28 mx-auto"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Mandatory">Mandatory</SelectItem>
                                <SelectItem value="Optional">Optional</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="px-3 py-2">
                            {pricingTiers.length > 1 && (
                              <Button type="button" variant="ghost" size="sm" onClick={() => removePricingTier(idx)} className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {pricingTiers.length > 1 && (
                  <p className="text-xs text-slate-500">
                    💡 Each tier will be saved as a separate transfer rate. They will be grouped by route and vehicle.
                  </p>
                )}
              </div>

              {/* VALIDITY Section */}
              <div className="space-y-4">
                <h4 className="text-sm font-medium text-slate-700 flex items-center gap-2">
                  <Calendar className="w-4 h-4" /> Validity
                </h4>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Valid From</Label>
                    <Input
                      type="date"
                      value={p2pForm.valid_from}
                      onChange={(e) => setP2pForm({ ...p2pForm, valid_from: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Valid To</Label>
                    <Input
                      type="date"
                      value={p2pForm.valid_to}
                      onChange={(e) => setP2pForm({ ...p2pForm, valid_to: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Season</Label>
                    <Input
                      value={p2pForm.season_name}
                      onChange={(e) => setP2pForm({ ...p2pForm, season_name: e.target.value })}
                      placeholder="e.g., Peak Season"
                    />
                  </div>
                </div>
              </div>

              {/* SUPPLIER & BEHAVIOR Section */}
              <div className="space-y-4">
                <h4 className="text-sm font-medium text-slate-700 flex items-center gap-2">
                  <Building2 className="w-4 h-4" /> Supplier & Settings
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Supplier</Label>
                    <Select value={p2pForm.supplier_id || ''} onValueChange={(v) => setP2pForm({ ...p2pForm, supplier_id: v })}>
                      <SelectTrigger><SelectValue placeholder="Select supplier" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="">None</SelectItem>
                        {suppliers.map(s => (
                          <SelectItem key={s.supplier_id} value={s.supplier_id}>
                            {s.supplier_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Include As *</Label>
                    <Select value={p2pForm.inclusion_type} onValueChange={(v) => setP2pForm({ ...p2pForm, inclusion_type: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {p2pMasters.inclusionTypes.map(t => (
                          <SelectItem key={t} value={t}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Internal Remarks</Label>
                  <Input
                    value={p2pForm.remarks}
                    onChange={(e) => setP2pForm({ ...p2pForm, remarks: e.target.value })}
                    placeholder="Notes for internal reference"
                  />
                </div>
              </div>
              
              <div className="flex gap-2 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setP2pDialogOpen(false)} className="flex-1">Cancel</Button>
                <Button type="submit" className="flex-1">{editingP2p ? 'Update Transfer' : 'Create Transfer'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* P2P Delete Confirmation Dialog */}
        <Dialog open={p2pDeleteConfirm.open} onOpenChange={(open) => !open && setP2pDeleteConfirm({ open: false, id: null, name: '' })}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Delete Transfer</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="text-gray-600">Are you sure you want to delete the transfer:</p>
              <p className="font-medium mt-2">{p2pDeleteConfirm.name}</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setP2pDeleteConfirm({ open: false, id: null, name: '' })} className="flex-1">
                Cancel
              </Button>
              <Button variant="destructive" onClick={() => handleDeleteP2p(p2pDeleteConfirm.id)} className="flex-1">
                Delete
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* P2P Bulk Upload Dialog */}
        <Dialog open={p2pBulkDialogOpen} onOpenChange={setP2pBulkDialogOpen}>
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle>Bulk Import P2P Transfers</DialogTitle>
              <DialogDescription>
                Upload an Excel file to import multiple transfer rates at once
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="flex items-start gap-3">
                  <FileSpreadsheet className="w-8 h-8 text-blue-600 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-blue-900">Step 1: Download Template</p>
                    <p className="text-sm text-blue-700 mt-1">
                      Get the template with sample data and fill in your transfer rates.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <input
                  type="file"
                  ref={p2pFileInputRef}
                  accept=".xlsx,.xls"
                  onChange={handleP2pFileUpload}
                  className="hidden"
                  id="p2p-file-upload"
                />
                <Upload className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600 mb-1 font-medium">
                  Step 2: Upload Your File
                </p>
                <p className="text-sm text-gray-500 mb-3">
                  {p2pUploading ? 'Processing your file...' : 'Select an Excel file (.xlsx)'}
                </p>
                <Button
                  variant="default"
                  onClick={() => p2pFileInputRef.current?.click()}
                  disabled={p2pUploading}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {p2pUploading ? (
                    <>
                      <span className="animate-spin mr-2">⏳</span>
                      Importing...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Choose Excel File
                    </>
                  )}
                </Button>
              </div>
              
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-sm font-medium text-gray-700 mb-2">Supported Columns:</p>
                <div className="text-xs text-gray-600 grid grid-cols-2 gap-x-4 gap-y-1">
                  <p><span className="font-medium text-red-600">*</span> From Place</p>
                  <p><span className="font-medium text-red-600">*</span> To Place</p>
                  <p><span className="font-medium text-red-600">*</span> Vehicle Type</p>
                  <p><span className="font-medium text-red-600">*</span> Sell Rate</p>
                  <p>Transport Mode</p>
                  <p>Variant Name</p>
                  <p>Transfer Category</p>
                  <p>Pricing Type</p>
                  <p>Max Pax</p>
                  <p>Cost Rate</p>
                  <p>GST %</p>
                  <p>Inclusion Type</p>
                  <p>Valid From/To</p>
                  <p>Season Name</p>
                </div>
                <p className="text-xs text-gray-500 mt-2 italic">
                  See Instructions sheet in template for full details
                </p>
              </div>
              
              <div className="flex gap-2">
                <Button variant="outline" onClick={handleP2pDownloadTemplate} className="flex-1">
                  <Download className="w-4 h-4 mr-2" />
                  Download Template
                </Button>
                <Button variant="outline" onClick={() => setP2pBulkDialogOpen(false)} className="flex-1">
                  Close
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default TransferManager;
